import { useLocale } from '@koopajs/react';
import { Box, Breadcrumbs, Link, Typography, Button } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useResourceList } from 'components/temp/useResourceListTemp';
import { PageContainer } from 'components/temp/PageContainer';
import { ICommittee } from 'types';
import { IFile, UploadedResolutionCard } from 'components/UploadedResolutions/UploadedResolutionCard';
import { useAppContext, usePermissions } from '@koopajs/react';
import { Prompt, Link as RouterLink } from 'react-router-dom';
import { EmptyState } from 'components/EmptyState';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { UploadResolutionButton } from '../components/UploadedResolutions/UploadedResolutionButton';

export const UploadedResolutions: React.FC = () => {
  const isUserWorkspaceOwner = usePermissions({ requiredPermissions: 'owner' });

  const history = useHistory();

  // redirect to dashboard if user is not workspace owner
  if (!isUserWorkspaceOwner) {
    history.push('/');
  }

  const { t } = useLocale();
  const keyPrefix = 'UploadedResolutions';

  const { getComponentContext } = useAppContext();
  const { files }: { files: IFile[] } = getComponentContext('uploadedResolutions') as { files: IFile[] };

  const { resources: committees } = useResourceList<ICommittee>({
    path: '/committees'
  });

  const areAllResolutionsAddedToBooks = files?.every((file) => file.id);

  //browser alert when user tries to leave the page with unsaved changes
  useEffect(() => {
    if (areAllResolutionsAddedToBooks) return;

    function beforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
    }

    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [areAllResolutionsAddedToBooks]);

  if (!files || files.length === 0)
    return (
      <EmptyState
        title={t(keyPrefix + '.EmptyState.title')}
        description={
          <Typography sx={{ whiteSpace: 'pre-line' }}>{t(keyPrefix + '.EmptyState.description')}</Typography>
        }
        button={<>{isUserWorkspaceOwner && <UploadResolutionButton variant="contained" />}</>}
      />
    );
  return (
    <>
      <Helmet>
        <title>{`${t('common:navigation.resolutions')} - Panorama`}</title>
      </Helmet>
      <Prompt when={!areAllResolutionsAddedToBooks} message={t(keyPrefix + '.unsavedChangesMessage')} />
      <PageContainer sxChildren={{ px: { xs: '24px', lg: '48px' }, py: '24px' }} className="rr-block">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Link
              underline="hover"
              color="inherit"
              component={RouterLink}
              to={'/resolutions'}
              sx={{ cursor: 'pointer' }}
            >
              {t(keyPrefix + '.resolutions')}
            </Link>
            <Typography color="text.primary">{t(keyPrefix + '.addResolutions')}</Typography>
          </Breadcrumbs>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'space-between' },
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 1, md: 0 }
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '24px' }}>
              {t(keyPrefix + '.title')}
            </Typography>

            <Button
              component={RouterLink}
              to="/resolutions"
              variant="contained"
              disabled={!areAllResolutionsAddedToBooks}
              data-cy="uploaded-resolutions_view-register-button"
            >
              <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
              {t(keyPrefix + '.viewRegisterLabel')}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {files?.length &&
              files.map((file: IFile, index) => {
                return (
                  <UploadedResolutionCard
                    file={file}
                    committees={committees}
                    index={index}
                    key={`${file.file.name} - ${index}`}
                  />
                );
              })}
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};
